import { useEffect, useState } from "react";
import axios from "axios";
import { X, Send, Copy, Check, Loader2, ExternalLink, Trash2, Mail, AlertTriangle, FileDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function ClassInsightsModal({ classes, onClose }) {
  const [classId, setClassId] = useState(classes?.[0]?.id || "");
  const [windowDays, setWindowDays] = useState(7);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Send-now form
  const [emails, setEmails] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [copied, setCopied] = useState(null);

  // Existing share tokens
  const [shares, setShares] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);

  const [tab, setTab] = useState("preview"); // preview | send | links

  useEffect(() => {
    if (!classId) return;
    setPreview(null);
    setPreviewError(null);
    setPreviewing(true);
    axios
      .get(`${API}/educator/class-insights/${classId}?days=${windowDays}`, authHeaders())
      .then((r) => setPreview(r.data))
      .catch((err) =>
        setPreviewError(err.response?.data?.detail || "Could not load preview")
      )
      .finally(() => setPreviewing(false));
  }, [classId, windowDays]);

  const loadShares = async () => {
    setSharesLoading(true);
    try {
      const res = await axios.get(`${API}/educator/class-insights/shares`, authHeaders());
      setShares(res.data || []);
    } catch {
      setShares([]);
    } finally {
      setSharesLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "links") loadShares();
  }, [tab]);

  const createShareLink = async () => {
    try {
      const res = await axios.post(
        `${API}/educator/class-insights/share/create`,
        { class_id: classId, label: "Manual share", expires_in_days: 60, days_window: windowDays },
        authHeaders()
      );
      setSendResult({
        ok: true,
        share_url: res.data.share_url,
        share_token: res.data.token,
        delivery: [],
        any_sent: false,
        manual: true,
      });
      setTab("send");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create share link");
    }
  };

  const handleSend = async () => {
    const list = emails
      .split(/[,\n;]+/)
      .map((s) => s.trim())
      .filter((s) => s && s.includes("@"));
    if (list.length === 0) {
      alert("Add at least one valid email address (comma- or newline-separated).");
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const res = await axios.post(
        `${API}/educator/class-insights/send`,
        { class_id: classId, to_emails: list, days_window: windowDays },
        authHeaders()
      );
      setSendResult(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send insights");
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  const revokeShare = async (token) => {
    if (!window.confirm("Revoke this link? Anyone with the URL will lose access.")) return;
    try {
      await axios.post(
        `${API}/educator/class-insights/share/${token}/revoke`,
        {},
        authHeaders()
      );
      loadShares();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not revoke");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="class-insights-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 text-white p-5 sm:p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/20"
            data-testid="class-insights-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="text-[11px] uppercase tracking-widest opacity-90 font-semibold">
            LemonBot Phase 5
          </div>
          <h2 className="text-2xl font-extrabold mt-1">Class Insights Digest</h2>
          <p className="text-sm opacity-95 mt-1 max-w-md">
            Weekly summary of top question themes, pitch growth, and class progress — built for
            educators, principals, and parents.
          </p>
        </div>

        {/* Class + window pickers */}
        <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-amber-50/40">
          <div className="flex flex-wrap gap-3 items-center">
            <select
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              className="bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-400"
              data-testid="class-insights-class-select"
            >
              {(classes || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <div className="flex items-center bg-white border border-slate-300 rounded-lg overflow-hidden">
              {[7, 14, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => setWindowDays(d)}
                  className={`px-3 py-1.5 text-sm font-semibold ${
                    windowDays === d
                      ? "bg-amber-400 text-white"
                      : "text-slate-600 hover:bg-amber-50"
                  }`}
                  data-testid={`class-insights-window-${d}`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 px-5 sm:px-6 flex gap-1">
          {[
            { k: "preview", label: "Preview" },
            { k: "send", label: "Send" },
            { k: "links", label: "Active Links" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-3 py-2.5 text-sm font-semibold border-b-2 ${
                tab === t.k
                  ? "border-amber-500 text-amber-700"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`class-insights-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          {tab === "preview" && (
            <PreviewBody preview={preview} loading={previewing} error={previewError} />
          )}
          {tab === "send" && (
            <SendBody
              emails={emails}
              setEmails={setEmails}
              sending={sending}
              onSend={handleSend}
              onCreateLinkOnly={createShareLink}
              result={sendResult}
              copied={copied}
              onCopy={copyToClipboard}
            />
          )}
          {tab === "links" && (
            <LinksBody
              loading={sharesLoading}
              shares={shares}
              copied={copied}
              onCopy={copyToClipboard}
              onRevoke={revokeShare}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewBody({ preview, loading, error }) {
  if (loading)
    return <PreviewSkeleton />;
  if (error)
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700">
        {error}
      </div>
    );
  if (!preview) return null;
  const t = preview.totals || {};
  const c = preview.completion || {};
  const p = preview.pitch_growth || {};

  const downloadPdf = async () => {
    try {
      const resp = await axios.get(
        `${API}/educator/class-insights/${preview.class_id}/pdf?days=${preview.window_days || 7}`,
        { ...authHeaders(), responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = (preview.class_name || "Class").replace(/[^a-z0-9_-]/gi, "_").slice(0, 40);
      a.download = `Class_Insights_${safe}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert("Could not download PDF.");
    }
  };

  return (
    <div className="space-y-4" data-testid="class-insights-preview-body">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
          Live preview
        </div>
        <button
          onClick={downloadPdf}
          className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-amber-400 text-slate-700 hover:text-amber-700 text-sm font-semibold rounded-lg px-3 py-1.5"
          data-testid="class-insights-pdf-btn"
        >
          <FileDown className="w-4 h-4" />
          Download PDF
        </button>
      </div>
      {preview.narrative && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-xl">
          <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-1">
            AI summary
          </div>
          <p className="text-slate-800 leading-relaxed">{preview.narrative}</p>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Students" value={t.students || 0} />
        <Stat label="Lessons (week)" value={t.lessons_completed || 0} />
        <Stat label="Avg Completion" value={`${c.avg_pct || 0}%`} />
        <Stat label="Pitch Growth" value={`+${p.delta || 0}%`} accent="text-emerald-600" />
      </div>
      <Section title="Top Themes">
        {(preview.top_themes || []).length === 0 ? (
          <p className="text-slate-500 text-sm italic">No common questions yet.</p>
        ) : (
          <ul className="space-y-2">
            {preview.top_themes.map((th, i) => (
              <li
                key={`${th.label}-${i}`}
                className="flex items-start gap-2 text-sm"
                data-testid={`preview-theme-${i}`}
              >
                <span className="font-bold text-amber-700">{th.label}</span>
                <span className="text-slate-500">({th.count})</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Top Movers">
        {(preview.top_movers || []).length === 0 ? (
          <p className="text-slate-500 text-sm italic">
            No pitch growth yet — encourage Pitch Practice.
          </p>
        ) : (
          <ul className="space-y-2">
            {preview.top_movers.map((m, i) => (
              <li key={`${m.name}-${i}`} className="text-sm" data-testid={`preview-mover-${i}`}>
                <span className="font-bold text-slate-900">{m.name}</span>{" "}
                <span className="text-emerald-600 font-semibold">+{m.delta}%</span>{" "}
                <span className="text-slate-500">({m.first_pct}% → {m.best_pct}%)</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function PreviewSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" data-testid="class-insights-preview-skeleton">
      <div className="h-3 w-24 bg-slate-200 rounded" />
      <div className="bg-amber-50 border-l-4 border-amber-200 p-4 rounded-r-xl space-y-2">
        <div className="h-2 w-20 bg-amber-200 rounded" />
        <div className="h-3 w-full bg-amber-200/70 rounded" />
        <div className="h-3 w-5/6 bg-amber-200/70 rounded" />
        <div className="h-3 w-3/5 bg-amber-200/70 rounded" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-3 space-y-2">
            <div className="h-2 w-12 bg-slate-200 rounded" />
            <div className="h-7 w-14 bg-slate-200 rounded" />
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <div className="h-2 w-20 bg-slate-200 rounded" />
        <div className="h-3 w-3/4 bg-slate-100 rounded" />
        <div className="h-3 w-2/3 bg-slate-100 rounded" />
        <div className="h-3 w-3/5 bg-slate-100 rounded" />
      </div>
    </div>
  );
}

function SendBody({ emails, setEmails, sending, onSend, onCreateLinkOnly, result, copied, onCopy }) {
  const flagOff = result && result.email_enabled === false;
  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-bold text-slate-700 block mb-1.5">
          Send to (comma- or line-separated)
        </label>
        <textarea
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          rows={3}
          placeholder="principal@school.edu, parent@example.com"
          className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          data-testid="class-insights-emails-input"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={onSend}
          disabled={sending}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl px-5 py-2.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="class-insights-send-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? "Sending…" : "Send Now"}
        </button>
        <button
          onClick={onCreateLinkOnly}
          className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-700 font-semibold rounded-xl px-4 py-2.5 hover:bg-slate-50"
          data-testid="class-insights-link-only-btn"
        >
          Just create a share link
        </button>
      </div>

      {result && (
        <div
          className={`rounded-xl border p-4 ${
            flagOff
              ? "bg-amber-50 border-amber-200"
              : "bg-emerald-50 border-emerald-200"
          }`}
          data-testid="class-insights-send-result"
        >
          {flagOff ? (
            <div className="flex gap-2 items-start text-sm text-amber-900">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-bold mb-1">Email delivery is currently disabled.</div>
                <p className="leading-relaxed">
                  We saved a public share link below. Once your SendGrid key is added and the{" "}
                  <code className="bg-amber-100 px-1 rounded">CLASS_INSIGHTS_EMAIL_ENABLED</code>{" "}
                  flag is on, the same button will deliver to your recipients.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 items-start text-sm text-emerald-900">
              <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-bold mb-1">
                  {result.any_sent
                    ? `Sent to ${result.delivery.filter((d) => d.sent).length} recipient${
                        result.delivery.filter((d) => d.sent).length === 1 ? "" : "s"
                      }`
                    : "Share link is ready"}
                </div>
                {result.delivery.length > 0 && (
                  <ul className="text-xs space-y-0.5">
                    {result.delivery.map((d) => (
                      <li key={d.to}>
                        <span className="font-mono">{d.to}</span> ·{" "}
                        <span className={d.sent ? "text-emerald-700" : "text-slate-500"}>
                          {d.sent ? "sent" : d.queued ? "queued" : "failed"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          {result.share_url && (
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <input
                readOnly
                value={result.share_url}
                onClick={(e) => e.target.select()}
                className="flex-1 min-w-[220px] bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-mono"
                data-testid="class-insights-share-url"
              />
              <button
                onClick={() => onCopy(result.share_url, "send")}
                className="inline-flex items-center gap-1 bg-slate-900 text-white text-xs font-semibold rounded-lg px-3 py-1.5 hover:bg-slate-700"
                data-testid="class-insights-copy-btn"
              >
                {copied === "send" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied === "send" ? "Copied" : "Copy"}
              </button>
              <a
                href={result.share_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LinksBody({ loading, shares, copied, onCopy, onRevoke }) {
  if (loading)
    return (
      <div className="flex items-center justify-center py-8 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  if (!shares || shares.length === 0)
    return (
      <p className="text-sm text-slate-500 italic">
        No share links yet. Use the Send tab to create one.
      </p>
    );
  return (
    <ul className="space-y-2">
      {shares.map((s) => {
        const url = `${window.location.origin}/insights/${s.token}`;
        const expired =
          s.expires_at && new Date(s.expires_at) < new Date();
        const inactive = s.revoked || expired;
        return (
          <li
            key={s.token}
            className={`border rounded-xl p-3 ${
              inactive ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200"
            }`}
            data-testid={`class-insights-share-row-${s.token.slice(0, 6)}`}
          >
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 text-sm truncate">
                  {s.label || "Share link"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {s.view_count || 0} views ·{" "}
                  {s.revoked ? (
                    <span className="text-rose-600 font-semibold">REVOKED</span>
                  ) : expired ? (
                    <span className="text-amber-600 font-semibold">EXPIRED</span>
                  ) : (
                    <>Valid through {formatDate(s.expires_at)}</>
                  )}{" "}
                  · {s.days_window || 7}-day window
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  disabled={inactive}
                  onClick={() => onCopy(url, s.token)}
                  className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2 py-1.5 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {copied === s.token ? (
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  {copied === s.token ? "Copied" : "Copy"}
                </button>
                {!inactive && (
                  <button
                    onClick={() => onRevoke(s.token)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 rounded-lg px-2 py-1.5 hover:bg-rose-50"
                    data-testid={`class-insights-revoke-${s.token.slice(0, 6)}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Revoke
                  </button>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Stat({ label, value, accent = "text-amber-700" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-extrabold ${accent}`}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-[11px] uppercase tracking-wider text-slate-600 font-bold mb-2">
        {title}
      </h3>
      {children}
    </div>
  );
}
